import { useState } from 'react';
import { toast } from 'react-toastify';
import * as partnerApis from 'apis/partners';
import './PartnerApplyStatic.scss';

/*
  "Become a Partner" layout, converted from the partners.html mockup
  (kept at the repo root for design reference/backup).
  Self-contained component, all CSS scoped under ".pa-page" with
  "pa-" prefixed classes, mirroring how PlansListStatic/pp-* was done.

  On submit, posts to POST /contact-form with type: 'partner' (see
  apis/partners#submitPartnerApplication and the taurian-backend
  ContactFormType enum) so applications land alongside generic
  lead/support messages but stay distinguishable.
*/

const EMPTY_FORM = { name: '', email: '', phone: '', category: '', message: '' };

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidPhone = (value) => /^[0-9+\-\s()]{7,15}$/.test(value);

const PartnerApplyStatic = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (form.name.trim().length < 2) nextErrors.name = 'Please enter your name';
    if (!isValidEmail(form.email.trim())) nextErrors.email = 'Please enter a valid email';
    if (!isValidPhone(form.phone.trim())) nextErrors.phone = 'Please enter a valid phone number';
    if (form.message.trim().length < 10) nextErrors.message = 'Please enter a message (min. 10 characters)';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSubmitted(false);
      return;
    }

    setSubmitting(true);
    try {
      await partnerApis.submitPartnerApplication({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        category: form.category.trim(),
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm(EMPTY_FORM);
      setErrors({});
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pa-page">

      {/* ===== HERO ===== */}
      <div className="pa-hero">
        <div>
          <h1>BECOME A PARTNER</h1>
          <p>Team up with Taurian to put your brand in front of thousands of engaged riders and enthusiasts. Offer exclusive perks, grow your reach, and be part of India&apos;s most exciting rewards platform.</p>
        </div>
      </div>

      <div className="pa-container">

        {/* ===== WHY PARTNER / BENEFITS STRIP ===== */}
        <div className="pa-section-header">
          <i className="fa-solid fa-bars"></i>
          <h2>WHY PARTNER WITH US</h2>
          <div className="pa-spacer"></div>
        </div>

        <div className="pa-trust-strip">

          <div className="pa-trust-item">
            <i className="fa-solid fa-users"></i>
            <h4>ENGAGED AUDIENCE</h4>
            <p>Get discovered by thousands of active members every month</p>
          </div>

          <div className="pa-trust-item">
            <i className="fa-solid fa-bullhorn"></i>
            <h4>BRAND VISIBILITY</h4>
            <p>Featured placement across giveaways, events and offers</p>
          </div>

          <div className="pa-trust-item">
            <i className="fa-solid fa-handshake"></i>
            <h4>CO-MARKETING</h4>
            <p>Joint campaigns, exclusive coupons and cross-promotions</p>
          </div>

          <div className="pa-trust-item">
            <i className="fa-solid fa-headset"></i>
            <h4>DEDICATED SUPPORT</h4>
            <p>A dedicated account manager to help you get set up fast</p>
          </div>

        </div>

        {/* ===== APPLICATION ===== */}
        <div className="pa-section-header">
          <i className="fa-solid fa-bars"></i>
          <h2>PARTNER APPLICATION</h2>
          <div className="pa-spacer"></div>
        </div>

        <div className="pa-apply-grid">

          {/* ---- Info panel ---- */}
          <div className="pa-info-panel">
            <h3>Let&apos;s build something great together</h3>
            <p>Whether you&apos;re a brand, a venue or a service provider, tell us a bit about your business and our partnerships team will get back to you within 2 business days.</p>

            <ul className="pa-info-list">
              <li><i className="fa-solid fa-circle-check"></i>No cost to apply, no long-term lock-in</li>
              <li><i className="fa-solid fa-circle-check"></i>Access to our full partner dashboard</li>
              <li><i className="fa-solid fa-circle-check"></i>Run offers, coupons, events &amp; giveaways</li>
              <li><i className="fa-solid fa-circle-check"></i>Track performance with real-time analytics</li>
            </ul>

            <div className="pa-info-divider"></div>

            <div className="pa-contact-row">
              <div className="pa-icon-circle"><i className="fa-solid fa-envelope"></i></div>
              <div>
                <span className="pa-label">Email Us</span>
                <span className="pa-value">partners@thetaurian.com</span>
              </div>
            </div>

            <div className="pa-contact-row">
              <div className="pa-icon-circle"><i className="fa-solid fa-phone"></i></div>
              <div>
                <span className="pa-label">Call Us</span>
                <span className="pa-value">+91 99999 99999</span>
              </div>
            </div>

          </div>

          {/* ---- Form panel ---- */}
          <div className="pa-form-panel">

            <h3>Tell us about your business</h3>
            <p className="pa-form-sub">Fill in your details below and we&apos;ll reach out to discuss partnership opportunities.</p>

            {submitted && (
              <div className="pa-success-box">
                <i className="fa-solid fa-circle-check"></i>
                <div>
                  <h4>Application received!</h4>
                  <p>Thanks for your interest — our team will contact you shortly.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>

              <div className="pa-form-row">
                <div className={`pa-field ${errors.name ? 'pa-has-error' : ''}`}>
                  <label htmlFor="pa-name">Full Name</label>
                  <input
                    type="text"
                    id="pa-name"
                    placeholder="e.g. John Doe"
                    value={form.name}
                    onChange={handleChange('name')}
                  />
                  {errors.name && <span className="pa-error-text">{errors.name}</span>}
                </div>

                <div className={`pa-field ${errors.email ? 'pa-has-error' : ''}`}>
                  <label htmlFor="pa-email">Email Address</label>
                  <input
                    type="email"
                    id="pa-email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={handleChange('email')}
                  />
                  {errors.email && <span className="pa-error-text">{errors.email}</span>}
                </div>
              </div>

              <div className={`pa-field ${errors.phone ? 'pa-has-error' : ''}`}>
                <label htmlFor="pa-phone">Phone Number</label>
                <input
                  type="tel"
                  id="pa-phone"
                  placeholder="e.g. 99999 99999"
                  value={form.phone}
                  onChange={handleChange('phone')}
                />
                {errors.phone && <span className="pa-error-text">{errors.phone}</span>}
              </div>

              <div className="pa-field">
                <label htmlFor="pa-category">Business Category <span className="pa-optional">(optional)</span></label>
                <input
                  type="text"
                  id="pa-category"
                  placeholder="e.g. Automotive, Retail, Food & Beverage"
                  value={form.category}
                  onChange={handleChange('category')}
                />
              </div>

              <div className={`pa-field ${errors.message ? 'pa-has-error' : ''}`}>
                <label htmlFor="pa-message">Message</label>
                <textarea
                  id="pa-message"
                  placeholder="Tell us about your business and how you'd like to partner with Taurian..."
                  value={form.message}
                  onChange={handleChange('message')}
                ></textarea>
                {errors.message && <span className="pa-error-text">{errors.message}</span>}
              </div>

              <button type="submit" className="pa-submit-btn" disabled={submitting}>
                {submitting ? (
                  <>SUBMITTING... <i className="fa-solid fa-spinner fa-spin"></i></>
                ) : (
                  <>SUBMIT APPLICATION <i className="fa-solid fa-arrow-right"></i></>
                )}
              </button>

              <p className="pa-form-note">By submitting this form, you agree to be contacted by the Taurian partnerships team regarding your application.</p>

            </form>

          </div>

        </div>

      </div>

    </div>
  );
};

export default PartnerApplyStatic;
